import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";

interface AccountForm {
  name: string; legalName: string; entity: string; type: string;
  industry: string; size: string; employeeCount: string; annualRevenue: string;
  website: string; linkedinUrl: string; phone: string;
  rating: string; healthScore: string;
  tags: string; geography: string; cbcOfferings: string; solutions: string; accountDegree: string;
  billingStreet: string; billingCity: string; billingState: string; billingCode: string; billingCountry: string;
  shippingStreet: string; shippingCity: string; shippingState: string; shippingCode: string; shippingCountry: string;
  description: string;
  proposalSent: boolean; proposalSentDate: string; proposalFollowupDate: string; winningProbability: string;
  locked: boolean;
  fax: string; tickerSymbol: string; ownership: string; territory: string; sicCode: string;
}

const EMPTY: AccountForm = {
  name: "", legalName: "", entity: "BOTH", type: "PROSPECT",
  industry: "", size: "", employeeCount: "", annualRevenue: "",
  website: "", linkedinUrl: "", phone: "",
  rating: "", healthScore: "",
  tags: "", geography: "", cbcOfferings: "", solutions: "", accountDegree: "",
  billingStreet: "", billingCity: "", billingState: "", billingCode: "", billingCountry: "",
  shippingStreet: "", shippingCity: "", shippingState: "", shippingCode: "", shippingCountry: "",
  description: "",
  proposalSent: false, proposalSentDate: "", proposalFollowupDate: "", winningProbability: "",
  locked: false,
  fax: "", tickerSymbol: "", ownership: "", territory: "", sicCode: "",
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader className="pb-3"><CardTitle className="text-sm">{title}</CardTitle></CardHeader>
      <CardContent className="grid gap-4 pt-0 md:grid-cols-2">{children}</CardContent>
    </Card>
  );
}

function Field({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
  return <div><Label htmlFor={id}>{label}</Label>{children}</div>;
}

export default function AccountNewPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<AccountForm>(EMPTY);

  function set<K extends keyof AccountForm>(k: K, v: AccountForm[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const payload: any = {};
      for (const [k, v] of Object.entries(form)) {
        if (v === "" || v === null || v === undefined) continue;
        if (k === "tags") payload.tags = (v as string).split(",").map((s) => s.trim()).filter(Boolean);
        else if (["employeeCount", "annualRevenue", "healthScore", "winningProbability"].includes(k)) payload[k] = Number(v);
        else payload[k] = v;
      }
      const { data } = await api.post("/accounts", payload);
      qc.invalidateQueries({ queryKey: ["/accounts"] });
      navigate(`/accounts/${data.id}`);
    } catch (err: any) {
      const resp = err?.response?.data;
      setError(resp?.message ?? resp?.error ?? "Failed to create account.");
    } finally { setBusy(false); }
  }

  return (
    <>
      <PageHeader title="New Account" description="Full Zoho-style account record." />
      <form onSubmit={submit} className="p-6">
        <div className="mx-auto max-w-4xl space-y-4">
          <Section title="Identity">
            <Field id="name" label="Account name *">
              <Input id="name" required value={form.name} onChange={(e) => set("name", e.target.value)} />
            </Field>
            <Field id="legalName" label="Legal name">
              <Input id="legalName" value={form.legalName} onChange={(e) => set("legalName", e.target.value)} />
            </Field>
            <Field id="type" label="Type">
              <Select id="type" value={form.type} onChange={(e) => set("type", e.target.value)}>
                <option value="PROSPECT">Prospect</option>
                <option value="CLIENT">Client</option>
                <option value="PARTNER">Partner</option>
                <option value="GOVERNMENT">Government</option>
              </Select>
            </Field>
            <Field id="entity" label="Entity">
              <Select id="entity" value={form.entity} onChange={(e) => set("entity", e.target.value)}>
                <option value="BOTH">Both</option>
                <option value="US">US</option>
                <option value="INDIA">India</option>
                <option value="GLOCALIZE">Glocalize</option>
              </Select>
            </Field>
            <Field id="rating" label="Rating">
              <Select id="rating" value={form.rating} onChange={(e) => set("rating", e.target.value)}>
                <option value="">—</option>
                <option value="HOT">Hot</option>
                <option value="WARM">Warm</option>
                <option value="COLD">Cold</option>
              </Select>
            </Field>
            <Field id="healthScore" label="Health score (0–100)">
              <Input id="healthScore" type="number" min="0" max="100" value={form.healthScore} onChange={(e) => set("healthScore", e.target.value)} />
            </Field>
          </Section>

          <Section title="Company profile">
            <Field id="industry" label="Industry">
              <Input id="industry" value={form.industry} onChange={(e) => set("industry", e.target.value)} />
            </Field>
            <Field id="size" label="Size range">
              <Input id="size" placeholder="e.g. 51-200" value={form.size} onChange={(e) => set("size", e.target.value)} />
            </Field>
            <Field id="employeeCount" label="Employees (count)">
              <Input id="employeeCount" type="number" min="0" value={form.employeeCount} onChange={(e) => set("employeeCount", e.target.value)} />
            </Field>
            <Field id="annualRevenue" label="Annual revenue (USD)">
              <Input id="annualRevenue" type="number" min="0" value={form.annualRevenue} onChange={(e) => set("annualRevenue", e.target.value)} />
            </Field>
            <Field id="website" label="Website">
              <Input id="website" type="url" placeholder="https://…" value={form.website} onChange={(e) => set("website", e.target.value)} />
            </Field>
            <Field id="linkedinUrl" label="LinkedIn">
              <Input id="linkedinUrl" type="url" value={form.linkedinUrl} onChange={(e) => set("linkedinUrl", e.target.value)} />
            </Field>
            <Field id="phone" label="Phone">
              <Input id="phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            </Field>
          </Section>

          <Section title="Categorization">
            <Field id="geography" label="Geography">
              <Input id="geography" placeholder="APAC, EMEA, NA-East, …" value={form.geography} onChange={(e) => set("geography", e.target.value)} />
            </Field>
            <Field id="accountDegree" label="Partner tier / degree">
              <Input id="accountDegree" placeholder="Gold, Silver, Preferred, …" value={form.accountDegree} onChange={(e) => set("accountDegree", e.target.value)} />
            </Field>
            <Field id="solutions" label="Solutions">
              <Input id="solutions" placeholder="Data & AI, Cloud, Staffing, …" value={form.solutions} onChange={(e) => set("solutions", e.target.value)} />
            </Field>
            <Field id="cbcOfferings" label="CBC offerings">
              <Input id="cbcOfferings" value={form.cbcOfferings} onChange={(e) => set("cbcOfferings", e.target.value)} />
            </Field>
            <div className="md:col-span-2">
              <Label htmlFor="tags">Tags (comma-separated)</Label>
              <Input id="tags" placeholder="Strategic, Renewable, Tier-1, …" value={form.tags} onChange={(e) => set("tags", e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="description">Description</Label>
              <textarea
                id="description"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
              />
            </div>
          </Section>

          <Section title="Billing address">
            <div className="md:col-span-2">
              <Label htmlFor="billingStreet">Street</Label>
              <Input id="billingStreet" value={form.billingStreet} onChange={(e) => set("billingStreet", e.target.value)} />
            </div>
            <Field id="billingCity" label="City">
              <Input id="billingCity" value={form.billingCity} onChange={(e) => set("billingCity", e.target.value)} />
            </Field>
            <Field id="billingState" label="State / region">
              <Input id="billingState" value={form.billingState} onChange={(e) => set("billingState", e.target.value)} />
            </Field>
            <Field id="billingCode" label="ZIP / postcode">
              <Input id="billingCode" value={form.billingCode} onChange={(e) => set("billingCode", e.target.value)} />
            </Field>
            <Field id="billingCountry" label="Country">
              <Input id="billingCountry" value={form.billingCountry} onChange={(e) => set("billingCountry", e.target.value)} />
            </Field>
          </Section>

          <Section title="Shipping address (optional)">
            <div className="md:col-span-2">
              <Label htmlFor="shippingStreet">Street</Label>
              <Input id="shippingStreet" value={form.shippingStreet} onChange={(e) => set("shippingStreet", e.target.value)} />
            </div>
            <Field id="shippingCity" label="City">
              <Input id="shippingCity" value={form.shippingCity} onChange={(e) => set("shippingCity", e.target.value)} />
            </Field>
            <Field id="shippingState" label="State / region">
              <Input id="shippingState" value={form.shippingState} onChange={(e) => set("shippingState", e.target.value)} />
            </Field>
            <Field id="shippingCode" label="ZIP / postcode">
              <Input id="shippingCode" value={form.shippingCode} onChange={(e) => set("shippingCode", e.target.value)} />
            </Field>
            <Field id="shippingCountry" label="Country">
              <Input id="shippingCountry" value={form.shippingCountry} onChange={(e) => set("shippingCountry", e.target.value)} />
            </Field>
          </Section>

          <Section title="Proposal & deal tracking">
            <div className="flex items-center gap-2">
              <input id="proposalSent" type="checkbox" checked={form.proposalSent} onChange={(e) => set("proposalSent", e.target.checked)} />
              <Label htmlFor="proposalSent">Proposal sent</Label>
            </div>
            <Field id="winningProbability" label="Winning probability (%)">
              <Input id="winningProbability" type="number" min="0" max="100" value={form.winningProbability} onChange={(e) => set("winningProbability", e.target.value)} />
            </Field>
            <Field id="proposalSentDate" label="Proposal sent date">
              <Input id="proposalSentDate" type="date" value={form.proposalSentDate} onChange={(e) => set("proposalSentDate", e.target.value)} />
            </Field>
            <Field id="proposalFollowupDate" label="Followup date">
              <Input id="proposalFollowupDate" type="date" value={form.proposalFollowupDate} onChange={(e) => set("proposalFollowupDate", e.target.value)} />
            </Field>
          </Section>

          <Section title="Additional information">
            <Field id="fax" label="Fax">
              <Input id="fax" value={form.fax} onChange={(e) => set("fax", e.target.value)} />
            </Field>
            <Field id="tickerSymbol" label="Ticker symbol">
              <Input id="tickerSymbol" placeholder="NASDAQ:MSFT" value={form.tickerSymbol} onChange={(e) => set("tickerSymbol", e.target.value)} />
            </Field>
            <Field id="ownership" label="Ownership">
              <Select id="ownership" value={form.ownership} onChange={(e) => set("ownership", e.target.value)}>
                <option value="">—</option>
                <option value="Private">Private</option>
                <option value="Public">Public</option>
                <option value="Subsidiary">Subsidiary</option>
                <option value="Government">Government</option>
                <option value="Non-profit">Non-profit</option>
              </Select>
            </Field>
            <Field id="territory" label="Territory">
              <Input id="territory" placeholder="NA-East, APAC-South…" value={form.territory} onChange={(e) => set("territory", e.target.value)} />
            </Field>
            <Field id="sicCode" label="SIC code">
              <Input id="sicCode" value={form.sicCode} onChange={(e) => set("sicCode", e.target.value)} />
            </Field>
          </Section>

          <Section title="Compliance">
            <div className="flex items-center gap-2">
              <input id="locked" type="checkbox" checked={form.locked} onChange={(e) => set("locked", e.target.checked)} />
              <Label htmlFor="locked">Lock record (read-only)</Label>
            </div>
          </Section>

          <div className="flex items-center justify-between rounded-md border bg-card px-6 py-4">
            {error ? <p className="text-sm text-destructive">{error}</p> : <span />}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => navigate("/accounts")}>Cancel</Button>
              <Button type="submit" disabled={busy || !form.name}>{busy ? "Saving…" : "Create"}</Button>
            </div>
          </div>
        </div>
      </form>
    </>
  );
}
